import { tokenScope } from '@nesso/identity-contracts/sign-in.commands';

import { apiClient } from '@/shared/api/http-client';

export type Status = 'idle' | 'connecting' | 'connected' | 'reconnecting';
export type StabilityStatus = 'stable' | 'unstable';

export const MAX_OPEN_ATTEMPT_COUNT = 3;
export const OPEN_ATTEMPT_DELAY = 500;
export const PING_INTERVAL = 90_000;
export const PONG_TIMEOUT = 10_000;
export const MAX_RECONNECT_DELAY = 30_000;
export const AUTH_TOKEN_TIMEOUT = 10_000;
export const MAX_RECONNECT_DURATION = 5 * 60_000;
export const STABILITY_RESET_DELAY = 10_000;
export const BUFFER_HIGH_THRESHOLD = 22_528 * 3; // 66KB - 3x the websocket max message size

export const TERMINAL_CLOSE_CODES = new Set<number>([
    4001, // Unauthorized — invalid or expired auth token
    4003, // Forbidden — insufficient permissions
    4009, // Session expired — server-side session no longer valid
]);

export class WebSocketClient {
    private websocket: WebSocket | undefined;
    private listenerAbortController: AbortController | undefined;
    private status: Status = 'idle';
    private wasEstablished = false;
    private openAttemptCount = 0;
    private retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private pingIntervalId: ReturnType<typeof setInterval> | undefined;
    private visibilityHandler: (() => void) | undefined;
    private pongTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private awaitingPong = false;
    private reconnectStartedAt: number | undefined;
    private openPromise: Promise<void> | undefined;
    private openResolve: (() => void) | undefined;
    private openReject: ((error: Error) => void) | undefined;

    private messageListeners: Set<(event: MessageEvent) => void> = new Set();
    private statusListeners: Set<(status: Status) => void> = new Set();
    private stabilityListeners: Set<(status: StabilityStatus) => void> = new Set();
    private stabilityResetTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private stabilityStatus: StabilityStatus = 'stable';

    constructor(private readonly url: string) {}

    private get isHealthy(): boolean {
        return this.status === 'connected' && this.websocket?.readyState === WebSocket.OPEN;
    }

    // Public API

    open(): Promise<void> {
        if (this.isHealthy) {
            return Promise.resolve();
        }

        // Stale: status is connected but readyState disagrees is not OPEN
        if (this.status === 'connected') {
            this.forceReconnect();
            return this.getOpenPromise();
        }

        const openPromise = this.getOpenPromise();

        // Already trying to connect/reconnect — return shared promise
        if (this.status === 'connecting' || this.status === 'reconnecting') {
            return openPromise;
        }

        this.setStatus('connecting');
        this.attemptToOpen();
        return openPromise;
    }

    async send<T>(action: string, data: T): Promise<void> {
        if (!this.isHealthy) {
            await this.open();
        }

        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not open after connection attempt');
        }

        this.websocket.send(JSON.stringify({ action, data }));
        this.checkStability();
    }

    close(): void {
        this.clearRetryTimeout();
        this.stopPingPong();
        this.stopStabilityReset();

        if (this.status === 'idle') {
            return;
        }

        this.abortListeners();
        this.websocket?.close();
        this.websocket = undefined;
        this.openAttemptCount = 0;
        this.reconnectStartedAt = undefined;
        this.wasEstablished = false;

        if (this.status !== 'connected') {
            const error = new Error('WebSocket connection aborted before open');
            this.openReject?.(error);
            this.cleanupOpenPromise();
        }

        this.setStatus('idle');
    }

    addMessageListener(
        callback: (event: MessageEvent) => void,
        options?: { signal?: AbortSignal },
    ): void {
        this.messageListeners.add(callback);
        options?.signal?.addEventListener('abort', () => this.messageListeners.delete(callback), {
            once: true,
        });
    }

    addStatusListener(
        callback: (status: Status) => void,
        options?: { signal?: AbortSignal },
    ): void {
        this.statusListeners.add(callback);
        options?.signal?.addEventListener('abort', () => this.statusListeners.delete(callback), {
            once: true,
        });
    }

    addStabilityListener(
        callback: (status: StabilityStatus) => void,
        options?: { signal?: AbortSignal },
    ): void {
        this.stabilityListeners.add(callback);
        options?.signal?.addEventListener('abort', () => this.stabilityListeners.delete(callback), {
            once: true,
        });
    }

    getStatus(): Status {
        return this.status;
    }

    getStabilityStatus(): StabilityStatus {
        return this.stabilityStatus;
    }

    // Connection lifecycle

    private async attemptToOpen(): Promise<void> {
        this.clearRetryTimeout();
        this.abortListeners();
        this.listenerAbortController = new AbortController();
        const { signal } = this.listenerAbortController;

        this.openAttemptCount += 1;

        console.debug('Attempting to open WebSocket connection. Attempt #', this.openAttemptCount);

        try {
            const token = await this.createAuthToken(signal);

            if (signal.aborted) return;

            this.websocket = new WebSocket(`${this.url}?token=${token}`);

            this.websocket.addEventListener('open', this.onOpen, { signal });
            this.websocket.addEventListener('error', this.onError, { signal });
            this.websocket.addEventListener('close', this.onClose, { signal });
            this.websocket.addEventListener('message', this.onMessage, { signal });
        } catch (error) {
            if (signal.aborted) return;
            this.handleAttemptFailure(error instanceof Error ? error : new Error(String(error)));
        }
    }

    private onOpen = (): void => {
        console.debug('WebSocket connection opened, readyState:', this.websocket?.readyState);
        this.openAttemptCount = 0;
        this.reconnectStartedAt = undefined;
        this.wasEstablished = true;
        this.setStatus('connected');
        this.startPingPong();
        this.sendPing();
        this.openResolve?.();
        this.cleanupOpenPromise();
    };

    private onError = (): void => {
        console.error(`WebSocket error. readyState: ${this.websocket?.readyState}`);
    };

    private onClose = (event: CloseEvent): void => {
        this.stopPingPong();
        this.stopStabilityReset();
        this.abortListeners();
        this.websocket = undefined;

        if (TERMINAL_CLOSE_CODES.has(event.code)) {
            this.wasEstablished = false;
            this.openAttemptCount = 0;
            this.reconnectStartedAt = undefined;
            this.setStatus('idle');
            this.openReject?.(
                new Error(`WebSocket closed with terminal code ${event.code}: ${event.reason}`),
            );
            this.cleanupOpenPromise();
            return;
        }

        if (this.wasEstablished) {
            // Post-establishment drop: retry with capped backoff and circuit breaker
            if (this.status !== 'reconnecting') {
                this.reconnectStartedAt = Date.now();
            }
            this.setStatus('reconnecting');
            this.scheduleReconnect();
        } else {
            // Initial connection failure: finite retry
            this.retryOrReject(
                new Error(
                    `WebSocket failed to connect, reason: ${event.reason}, code: ${event.code}`,
                ),
            );
        }
    };

    private onMessage = (event: MessageEvent): void => {
        // Any incoming message proves the connection is alive
        this.awaitingPong = false;
        this.clearPongTimeout();

        // Filter out pong messages — don't forward to consumers
        try {
            const data = JSON.parse(event.data);
            if (data.type === 'pong') {
                return;
            }
        } catch {
            // Not valid JSON — forward as regular message
        }

        this.messageListeners.forEach((listener) => listener(event));
    };

    // Initial retry (finite)

    private retryOrReject(error: Error): void {
        console.debug('Failed to open WebSocket connection:', error);

        if (this.openAttemptCount < MAX_OPEN_ATTEMPT_COUNT) {
            this.setStatus('connecting');
            const base = OPEN_ATTEMPT_DELAY * (1 + Math.log(this.openAttemptCount));
            const delay = base * (0.5 + Math.random());

            this.clearRetryTimeout();
            this.retryTimeoutId = setTimeout(() => {
                this.retryTimeoutId = undefined;
                if (this.status === 'connecting') {
                    this.attemptToOpen();
                }
            }, delay);
        } else {
            this.abortListeners();
            this.openAttemptCount = 0;
            this.setStatus('idle');
            this.openReject?.(error);
            this.cleanupOpenPromise();
        }
    }

    // Reconnection (infinite)

    private scheduleReconnect(): void {
        this.clearRetryTimeout();

        if (
            this.reconnectStartedAt !== undefined &&
            Date.now() - this.reconnectStartedAt >= MAX_RECONNECT_DURATION
        ) {
            console.debug('Reconnection circuit breaker tripped after max duration');
            this.reconnectStartedAt = undefined;
            this.wasEstablished = false;
            this.openAttemptCount = 0;
            this.setStatus('idle');
            this.openReject?.(new Error('WebSocket reconnection timed out'));
            this.cleanupOpenPromise();
            return;
        }

        const base = OPEN_ATTEMPT_DELAY * 2 ** this.openAttemptCount;
        const delay = Math.min(base, MAX_RECONNECT_DELAY) * (0.5 + Math.random());

        console.debug(`Scheduling reconnect in ${Math.round(delay)}ms`);

        this.retryTimeoutId = setTimeout(() => {
            this.retryTimeoutId = undefined;
            if (this.status === 'reconnecting') {
                this.attemptToOpen();
            }
        }, delay);
    }

    private forceReconnect(): void {
        this.stopPingPong();
        this.stopStabilityReset();
        this.abortListeners();

        try {
            this.websocket?.close();
        } catch {
            // Ignore close errors on zombie
        }
        this.websocket = undefined;

        this.wasEstablished = true;
        this.setStatus('reconnecting');
        if (this.reconnectStartedAt === undefined) {
            this.reconnectStartedAt = Date.now();
        }
        this.scheduleReconnect();
    }

    // Ping/pong health check

    private startPingPong(): void {
        this.stopPingPong();
        this.pingIntervalId = setInterval(() => this.sendPing(), PING_INTERVAL);

        if (typeof document !== 'undefined') {
            this.visibilityHandler = () => {
                if (document.visibilityState !== 'visible') return;
                if (this.status !== 'connected') return;

                if (!this.isHealthy) {
                    this.forceReconnect();
                    return;
                }

                this.sendPing();
            };
            document.addEventListener('visibilitychange', this.visibilityHandler);
        }
    }

    private sendPing(): void {
        if (!this.isHealthy) {
            return;
        }

        try {
            this.websocket?.send(JSON.stringify({ action: 'ping' }));
            this.awaitingPong = true;
            this.clearPongTimeout();
            this.pongTimeoutId = setTimeout(() => {
                this.pongTimeoutId = undefined;
                if (this.awaitingPong && this.status === 'connected') {
                    if (typeof document !== 'undefined' && document.hidden) {
                        this.awaitingPong = false;
                        return;
                    }
                    console.debug('Pong not received within timeout, forcing reconnect');
                    this.forceReconnect();
                }
            }, PONG_TIMEOUT);
        } catch {
            this.forceReconnect();
        }
    }

    private stopPingPong(): void {
        if (this.pingIntervalId !== undefined) {
            clearInterval(this.pingIntervalId);
            this.pingIntervalId = undefined;
        }
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = undefined;
        }
        this.clearPongTimeout();
        this.awaitingPong = false;
    }

    private clearPongTimeout(): void {
        if (this.pongTimeoutId !== undefined) {
            clearTimeout(this.pongTimeoutId);
            this.pongTimeoutId = undefined;
        }
    }

    // Stability check (send-driven, inactivity reset)

    private checkStability(): void {
        const backlogged = (this.websocket?.bufferedAmount ?? 0) > BUFFER_HIGH_THRESHOLD;
        this.setStabilityStatus(backlogged ? 'unstable' : 'stable');
        this.resetStabilityTimer();
    }

    private resetStabilityTimer(): void {
        if (this.stabilityResetTimeoutId !== undefined) {
            clearTimeout(this.stabilityResetTimeoutId);
        }
        this.stabilityResetTimeoutId = setTimeout(() => {
            this.stabilityResetTimeoutId = undefined;
            this.setStabilityStatus('stable');
        }, STABILITY_RESET_DELAY);
    }

    private stopStabilityReset(): void {
        if (this.stabilityResetTimeoutId !== undefined) {
            clearTimeout(this.stabilityResetTimeoutId);
            this.stabilityResetTimeoutId = undefined;
        }
        this.setStabilityStatus('stable');
    }

    private setStabilityStatus(newStatus: StabilityStatus): void {
        if (this.stabilityStatus === newStatus) return;
        this.stabilityStatus = newStatus;
        this.stabilityListeners.forEach((listener) => listener(newStatus));
    }

    // Auth

    private async createAuthToken(signal: AbortSignal): Promise<string> {
        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), AUTH_TOKEN_TIMEOUT);
        const combinedSignal = AbortSignal.any([signal, timeoutController.signal]);

        try {
            const { token } = await apiClient.post<{ token: string }>(
                '/auth/token',
                { scope: tokenScope.WEBSOCKET },
                combinedSignal,
            );
            return token;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    // Utils

    private handleAttemptFailure(error: Error): void {
        if (this.wasEstablished) {
            console.debug('Reconnect attempt failed:', error);
            this.scheduleReconnect();
        } else {
            this.retryOrReject(error);
        }
    }

    private clearRetryTimeout(): void {
        if (this.retryTimeoutId !== undefined) {
            clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = undefined;
        }
    }

    private abortListeners(): void {
        this.listenerAbortController?.abort();
        this.listenerAbortController = undefined;
    }

    private setStatus(newStatus: Status): void {
        if (this.status === newStatus) return;
        this.status = newStatus;
        this.statusListeners.forEach((listener) => listener(newStatus));
    }

    private getOpenPromise(): Promise<void> {
        if (!this.openPromise) {
            this.openPromise = new Promise<void>((resolve, reject) => {
                this.openResolve = resolve;
                this.openReject = reject;
            });
        }
        return this.openPromise;
    }

    private cleanupOpenPromise(): void {
        this.openPromise = undefined;
        this.openResolve = undefined;
        this.openReject = undefined;
    }
}

export const webSocketClient =
    import.meta.env.VITE_APP_ENV !== 'test'
        ? new WebSocketClient(import.meta.env.VITE_WEBSOCKET_URL)
        : (undefined as unknown as WebSocketClient);
